import ReactDom from 'react-dom'
import { useEffect, useRef } from 'react'

import { ModalType } from './types'
import css from './index.module.scss'


/** Компонент модального окна.
* @property {boolean} isOpen - Состояние модального окна, открытое или закрытое.
* @property {boolean} isEsc - Метка о допуске закрытия окна, при нажатии на кнопку Esc.
* @property {boolean} isWrapper - Метка о допуске закрытия окна, при клике вне области окна.
* @property {React.ReactNode} children - Дочерние элементы модального окна.
* @property {string} zIndex - Установка позиционирования модального окна.
* @property {string} overflow - Управление scroll.
* @property {string} maxWidth - Установка максимальной ширины окна.
* @property {() => void} onOk - Функция, которая будет вызываться, при нажатии на клавишу Enter.
* @property {() => void} onCancel - Функция, которая будет вызываться, при закрытии окна (необходима при использовании свойств: isEsc, isWrapper).
*/
export const Modal = ({ 
    isOpen, isEsc = false, isWrapper = false, children, className, zIndex = '1050', overflowY = 'hidden', maxWidth = '400px', ariaLabelledBy, onOk, onCancel
}: ModalType) => {
    const modalRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (isOpen && modalRef.current) {
            modalRef.current.focus() 
        }

        switch (isOpen) {
            case true: document.body.style.overflow = 'hidden'; break
            case false: document.body.style.overflow = 'unset'; break
        }
    }, [isOpen])

    useEffect(() => {
        let handleEsc: (event: KeyboardEvent) => void
        let handleEnter: (event: KeyboardEvent) => void

        if (isOpen) {
            handleEsc = (event: KeyboardEvent) => {
                if (isEsc && event.keyCode === 27) {
                    onCancel && onCancel()
                    document.body.style.overflow = 'unset'
                }
            }

            handleEnter = (event: KeyboardEvent) => {
                if (event.keyCode === 13) {
                    onOk && onOk()
                    document.body.style.overflow = 'unset'
                }
            }

            document.addEventListener('keydown', handleEsc)
            document.addEventListener('keydown', handleEnter)
        }

        return () => {
            document.removeEventListener('keydown', handleEsc)
            document.removeEventListener('keydown', handleEnter)
        }
    }, [isOpen, isEsc, onOk, onCancel])


    /** Зыкрытие окна, при клике мышкой вне области окна */
    const handlerCloseModal = () => {
        if (isWrapper) {
            onCancel && onCancel()
            document.body.style.overflow = 'unset'
        }
    }

    return ReactDom.createPortal(
        <div className={`${css.wrapper} ${isOpen ? css.open : css.close}`} style={{ width: '100%', zIndex: `${zIndex}`, overflowY: overflowY }} onClick={handlerCloseModal}>
            <div ref={modalRef} className={`${css.modal} ${className || ''}`.trim()} style={{ width: '100%', maxWidth: `${maxWidth ? maxWidth : 'max-content'}` }} role='dialog' aria-modal='true' aria-labelledby={ariaLabelledBy} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
                {children}
            </div>
        </div>, document.body
    )
}